import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Chip
} from '@mui/material';
// Import our custom Grid component instead
import Grid from '../components/Grid';
// Import the Supabase client and SentimentData type
import { SentimentService, SentimentData } from '../lib/supabase';

const SentimentPage: React.FC = () => {
  const [sentiment, setSentiment] = useState<SentimentData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  useEffect(() => {
    const fetchSentimentData = async () => {
      try {
        setLoading(true);
        
        // Fetch sentiment data from Supabase
        const sentimentData = await SentimentService.getLatestSentimentData();
        
        if (!sentimentData) {
          // Fallback to mock data if no data is available
          const mockData: SentimentData = {
            sentiment_usd: {
              percent_bullish: 50.0,
              percent_bearish: 35.0,
              relevant_tweet_count: 20
            },
            sentiment_xau: {
              percent_bullish: 56.3,
              percent_bearish: 25.0,
              relevant_tweet_count: 16
            }
          };
          
          setSentiment(mockData);
          setLastUpdated('Using mock data (No data from make.com found)');
        } else {
          setSentiment(sentimentData);
          setLastUpdated(new Date().toLocaleString());
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching sentiment data:', err);
        setError('Failed to fetch sentiment data from Supabase');
        setLoading(false);
      }
    };

    fetchSentimentData();

    // Refresh data every 5 minutes
    const refreshInterval = setInterval(fetchSentimentData, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  // Determine overall market sentiment label
  const determineTrend = (bullish: number, bearish: number): { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' } => {
    const diff = bullish - bearish;
    if (diff > 15) return { label: 'Strongly Bullish', color: 'success' };
    if (diff > 5) return { label: 'Mildly Bullish', color: 'success' };
    if (diff < -15) return { label: 'Strongly Bearish', color: 'error' };
    if (diff < -5) return { label: 'Mildly Bearish', color: 'error' };
    return { label: 'Neutral', color: 'info' };
  };

  const renderSentimentCard = (title: string, data: SentimentData['sentiment_usd' | 'sentiment_xau']) => {
    const trend = determineTrend(data.percent_bullish, data.percent_bearish);

    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="h3" gutterBottom align="center">
            {title}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption">Bullish</Typography>
            <Typography variant="caption">Bearish</Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={data.percent_bullish}
            color="primary"
            sx={{ height: 10, borderRadius: 5, mb: 1 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2">{data.percent_bullish.toFixed(1)}%</Typography>
            <Typography variant="body2">{data.percent_bearish.toFixed(1)}%</Typography>
          </Box>
          
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <Chip label={trend.label} color={trend.color} />
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            Based on AI-powered aggregation of expert opinions and news stories
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom color="secondary.main">
          Market Sentiment Analysis
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Expert consensus on Gold (XAU) and US Dollar (USD) market outlook
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Loading sentiment data...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
      ) : sentiment ? (
        <>
          {/* Summary Info */}
          <Paper elevation={2} sx={{ p: 2, mb: 4, backgroundColor: 'primary.light' }}>
            <Typography variant="h6" component="h2" gutterBottom align="center">Current Market Consensus</Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{ fontSize: '0.9rem', color: 'text.secondary' }}
            >
              <Typography variant="body2">Last updated: {lastUpdated}</Typography>
              <Typography variant="body2">Data sources: AI-powered analysis of expert opinions and market news</Typography>
            </Stack>
          </Paper>

          {/* Sentiment Cards Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              {renderSentimentCard('Gold (XAU) Sentiment', sentiment.sentiment_xau)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderSentimentCard('US Dollar (USD) Sentiment', sentiment.sentiment_usd)}
            </Grid>
          </Grid>

          {/* Explanation Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                About Our Sentiment Analysis
              </Typography>
              <Typography variant="body1" paragraph>
                XAUJournal calculates market sentiment by analyzing social media posts and commentary from leading financial experts. Our AI-powered system determines whether each post reflects bullish, bearish, or neutral sentiment towards Gold (XAU) and the US Dollar (USD).
              </Typography>
              <Typography variant="body1" paragraph>
                This sentiment analysis helps traders understand the prevailing market mood and can be used as one component of a comprehensive trading strategy. While sentiment is important, it should always be considered alongside technical analysis, fundamental data, and risk management practices.
              </Typography>
            </CardContent>
          </Card>
        </>
      ) : null}
    </Box>
  );
};

export default SentimentPage;